import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { CopyOutlined, DeleteOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import {
  AutoComplete,
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type {
  EarthingCompliance,
  EarthingMeasurement,
  ManualEarthingCompliance,
} from '../services/controlTaskService';
import { normalizeEarthingMeasurementDidNotTrip } from '../utils/earthingMeasurements';
import { calculateEarthingMeasurement, validateEarthingMeasuredValue } from '../../../utils/earthingMeasurementCalculation';
import type { NetworkType } from '../../../utils/networkType';
import ComplianceNoteTooltip from './ComplianceNoteTooltip';
import './EarthingMeasurementsTable.scss';

const { Text } = Typography;

type EarthingMeasurementsForm = {
  measurements: EarthingMeasurement[];
};

type TableRow = {
  key: number;
  name: number;
};

type RcdTripFieldName = 'rcdTripCurrentMa' | 'rcdTripTimeMs';

export type EarthingMeasurementsTableHandle = {
  validate: () => Promise<void>;
};

type EarthingMeasurementsTableProps = {
  value: EarthingMeasurement[];
  onChange: (value: EarthingMeasurement[]) => void;
  networkType?: NetworkType;
  disabled?: boolean;
};

const measurementPointGroupOptions = [
  { value: 'MAIN_PANEL', label: 'Ana Pano' },
  { value: 'SUB_PANEL', label: 'Tali Pano' },
  { value: 'FIRE_PANEL', label: 'Yangın Panosu' },
  { value: 'SOCKET', label: 'Priz' },
  { value: 'BODY', label: 'Gövde' },
];

const environmentConditionOptions = [
  { value: 'DRY', label: 'Kuru' },
  { value: 'WET', label: 'Islak' },
  { value: 'HUMID', label: 'Nemli' },
  { value: 'DUSTY', label: 'Tozlu' },
];

const tripCurveTypeOrModelOptions = ['B', 'C', 'D', 'TMŞ', 'NH3'].map((value) => ({
  value,
  label: value,
}));

const rcdRatingOptions = ['10', '30', '100', '300', '500'].map((value) => ({
  value,
  label: value,
}));

const protectiveConductorCrossSectionOptions = [
  '1', '1.5', '2', '4', '5', '6', '10', '16', '25', '35', '50', '70', '95', '120', '150', '185', '240',
].map((value) => ({
  value,
  label: value,
}));

const complianceTagColors: Partial<Record<EarthingCompliance, 'green' | 'red'>> = {
  NOT_1: 'green',
  NOT_2: 'red',
  NOT_3: 'red',
  NOT_4: 'green',
  NOT_5: 'red',
};

const manualComplianceOptions: Array<{ value: ManualEarthingCompliance; label: string }> = [
  { value: 'NOT_1', label: 'NOT 1' },
  { value: 'NOT_2', label: 'NOT 2' },
  { value: 'NOT_3', label: 'NOT 3' },
  { value: 'NOT_4', label: 'NOT 4' },
  { value: 'NOT_5', label: 'NOT 5' },
];

const isManualCompliance = (
  value?: EarthingCompliance | null,
): value is ManualEarthingCompliance => manualComplianceOptions.some(
  (option) => option.value === value,
);

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== '';

const nonNegativeRule = {
  validator: (_: unknown, value: unknown) => {
    if (!hasValue(value) || Number(value) >= 0) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Negatif değer girilemez.'));
  },
};

const nonNegativeNumericTextRule = {
  validator: (_: unknown, value: unknown) => {
    if (!hasValue(value)) {
      return Promise.resolve();
    }
    const text = String(value).trim();
    if (!text || !Number.isFinite(Number(text))) {
      return Promise.reject(new Error('Geçerli bir sayı giriniz.'));
    }
    if (Number(text) < 0) {
      return Promise.reject(new Error('Negatif değer girilemez.'));
    }
    return Promise.resolve();
  },
};

const renderCompliance = (value?: EarthingCompliance | null, ariaLabel?: string) => {
  const color = value ? complianceTagColors[value] : undefined;
  return value && color ? (
    <ComplianceNoteTooltip note={value} focusable ariaLabel={ariaLabel}>
      <Tag color={color}>{value.replace('_', ' ')}</Tag>
    </ComplianceNoteTooltip>
  ) : null;
};

const copyMeasurement = (
  networkType: NetworkType | undefined,
  source: EarthingMeasurement,
): EarthingMeasurement => {
  const copy = normalizeEarthingMeasurementDidNotTrip(source);
  delete copy.id;
  delete copy.reportNodeId;
  delete copy.rowOrder;
  delete copy.limitValueZsRsOhm;
  delete copy.limitValueCalculationStatus;
  delete copy.generatedCompliance;
  delete copy.compliance;
  delete copy.complianceOverridden;
  copy.clientKey = crypto.randomUUID();
  return calculateEarthingMeasurement(networkType, copy);
};

const EarthingMeasurementsTable = forwardRef<
  EarthingMeasurementsTableHandle,
  EarthingMeasurementsTableProps
>(({ value, onChange, networkType, disabled = false }, ref) => {
  const [form] = Form.useForm<EarthingMeasurementsForm>();
  const lastEmittedValueRef = useRef('');
  const previousNetworkTypeRef = useRef<NetworkType | undefined>(networkType);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      await form.validateFields();
    },
  }), [form]);

  useEffect(() => {
    const serializedValue = JSON.stringify(value ?? []);
    if (serializedValue === lastEmittedValueRef.current) {
      return;
    }
    form.setFieldValue('measurements', (value ?? []).map((row) => calculateEarthingMeasurement(
      networkType,
      normalizeEarthingMeasurementDidNotTrip(row),
    )));
    lastEmittedValueRef.current = serializedValue;
  }, [form, value, networkType]);

  const emitCurrentValue = useCallback((values: EarthingMeasurementsForm) => {
    const measurements = Array.isArray(values.measurements) ? values.measurements : [];
    lastEmittedValueRef.current = JSON.stringify(measurements);
    onChange(measurements);
  }, [onChange]);

  useEffect(() => {
    if (previousNetworkTypeRef.current === networkType) {
      return;
    }
    previousNetworkTypeRef.current = networkType;
    const measurements = form.getFieldValue('measurements');
    if (!Array.isArray(measurements)) {
      return;
    }
    const recalculated = measurements.map((measurement) => calculateEarthingMeasurement(networkType, measurement));
    form.setFieldsValue({ measurements: recalculated });
    emitCurrentValue({ measurements: recalculated });
  }, [emitCurrentValue, form, networkType]);

  const handleValuesChange = (
    changedValues: Partial<EarthingMeasurementsForm>,
    values: EarthingMeasurementsForm,
  ) => {
    const changedMeasurements = changedValues.measurements;
    const rowIndex = Array.isArray(changedMeasurements)
      ? changedMeasurements.findIndex((measurement) =>
        measurement !== undefined && measurement !== null,
      )
      : -1;
    const changedRow = rowIndex >= 0 ? changedMeasurements?.[rowIndex] : undefined;
    if (changedRow) {
      const measurements = values.measurements.map((measurement, index) => {
        if (index !== rowIndex) return measurement;
        const row = { ...measurement };
        if (Object.prototype.hasOwnProperty.call(changedRow, 'phaseEarthLoopImpedanceOhm')) {
          const text = String(changedRow.phaseEarthLoopImpedanceOhm ?? '').trim();
          row.phaseEarthLoopImpedanceDash = text === '-';
          row.phaseEarthLoopImpedanceOhm = text === '-' || !text ? null : text;
        }
        return calculateEarthingMeasurement(networkType, row);
      });
      form.setFieldValue(['measurements', rowIndex], measurements[rowIndex]);
      emitCurrentValue({ measurements });
      return;
    }
    emitCurrentValue(values);
  };

  const renderNumberInput = (rowIndex: number, fieldName: keyof EarthingMeasurement) => (
    <Form.Item name={[rowIndex, fieldName]}
      rules={[nonNegativeRule]}>

      <InputNumber
        aria-label={fieldName === 'ratedCurrentA' ? `In (A) satır ${rowIndex + 1}`
          : fieldName === 'toroidRatingA' ? `Toroid (A) satır ${rowIndex + 1}` : undefined}
        min={0}
        stringMode
        controls={false}
        disabled={disabled}
        className='earthing-measurements-table__number-input'
      />
    </Form.Item>
  );

  const validateRcdDependents = (rowIndex: number) => {
    window.setTimeout(() => {
      form.validateFields([
        ['measurements', rowIndex, 'rcdTripCurrentMa'],
        ['measurements', rowIndex, 'rcdTripTimeMs'],
      ]).catch(() => undefined);
    }, 0);
  };

  const emitAfterListOperation = () => {
    window.setTimeout(() => emitCurrentValue(form.getFieldsValue(true)), 0);
  };

  const toggleDidNotTrip = (rowIndex: number, checked: boolean) => {
    const measurements: EarthingMeasurement[] = form.getFieldValue('measurements') ?? [];
    const nextRow = calculateEarthingMeasurement(networkType, {
      ...measurements[rowIndex],
      rcdTripCurrentMa: null,
      rcdTripCurrentDidNotTrip: checked,
      rcdTripTimeMs: null,
      rcdTripTimeDidNotTrip: checked,
    });
    const nextMeasurements = measurements.map((measurement, index) =>
      index === rowIndex ? nextRow : measurement,
    );
    form.setFieldValue(['measurements', rowIndex], nextRow);
    emitCurrentValue({ measurements: nextMeasurements });
    window.setTimeout(() => form.validateFields([
      ['measurements', rowIndex, 'rcdTripCurrentMa'],
      ['measurements', rowIndex, 'rcdTripTimeMs'],
    ]).catch(() => undefined), 0);
  };

  const renderRcdDependentInput = (
    rowIndex: number,
    fieldName: RcdTripFieldName,
  ) => (
    <Form.Item
      noStyle
      shouldUpdate={(previous, current) => {
        return previous?.measurements?.[rowIndex]?.rcdRatingMa !==
          current?.measurements?.[rowIndex]?.rcdRatingMa
          || previous?.measurements?.[rowIndex]?.rcdTripCurrentDidNotTrip !==
          current?.measurements?.[rowIndex]?.rcdTripCurrentDidNotTrip
          || previous?.measurements?.[rowIndex]?.rcdTripTimeDidNotTrip !==
          current?.measurements?.[rowIndex]?.rcdTripTimeDidNotTrip;
      }}
    >
      {() => {
        const didNotTrip = form.getFieldValue([
          'measurements', rowIndex, 'rcdTripCurrentDidNotTrip',
        ]) === true || form.getFieldValue([
          'measurements', rowIndex, 'rcdTripTimeDidNotTrip',
        ]) === true;
        return (
          <div className='earthing-measurements-table__rcd-input'>
            <Form.Item
              name={[rowIndex, fieldName]}
              rules={[
                nonNegativeRule,
                {
                  validator: (_: unknown, fieldValue: unknown) => {
                    const rcdRating = form.getFieldValue(['measurements', rowIndex, 'rcdRatingMa']);
                    if (!hasValue(rcdRating) || hasValue(fieldValue) || didNotTrip) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('RCD değeri girildiğinde sayı veya AÇMADI zorunludur.'));
                  },
                },
              ]}
            >
              <InputNumber
                aria-label={fieldName === 'rcdTripCurrentMa'
                  ? `RCD açma akımı (mA) satır ${rowIndex + 1}`
                  : `RCD açma zamanı (ms) satır ${rowIndex + 1}`}
                min={0}
                stringMode
                controls={false}
                disabled={disabled || didNotTrip}
                className='earthing-measurements-table__number-input'
              />
            </Form.Item>
            {fieldName === 'rcdTripTimeMs' ? (
              <div className='earthing-measurements-table__did-not-trip'>
                <Switch
                  checked={didNotTrip}
                  disabled={disabled}
                  aria-label={`RCD AÇMADI satır ${rowIndex + 1}`}
                  onChange={(checked) => toggleDidNotTrip(rowIndex, checked)}
                />
                <Text>AÇMADI</Text>
              </div>
            ) : null}
          </div>
        );
      }}
    </Form.Item>
  );

  return (
    <section className='earthing-measurements-table'>
      <Text strong className='earthing-measurements-table__title'>
        TOPRAKLAMA GEÇİŞ DİRENCİ ÖLÇÜMLERİ
      </Text>
      <Form<EarthingMeasurementsForm>
        form={form}
        component={false}
        initialValues={{
          measurements: (value ?? []).map((row) => calculateEarthingMeasurement(
            networkType,
            normalizeEarthingMeasurementDidNotTrip(row),
          )),
        }}
        onValuesChange={handleValuesChange}
      >
        <Form.List name='measurements'>
          {(fields, { add, remove }) => {
            const dataSource: TableRow[] = fields.map((field) => ({
              key: field.key,
              name: field.name,
            }));
            const columns: ColumnsType<TableRow> = [
              {
                title: 'Sıra',
                width: 70,
                align: 'center',
                render: (_value, _record, index) => index + 1,
              },
              {
                title: 'Ölçüm Noktası Grubu',
                width: 170,
                render: (_value, row) => (
                  <Form.Item name={[row.name, 'measurementPointGroup']}>
                    <Select
                      allowClear
                      disabled={disabled}
                      options={measurementPointGroupOptions}
                      placeholder='Seçiniz'
                    />
                  </Form.Item>
                ),
              },
              {
                title: 'Ölçüm Noktası Tanımı - Açıklaması',
                width: 280,
                render: (_value, row) => (
                  <Form.Item name={[row.name, 'measurementPointDescription']}>
                    <Input.TextArea aria-label={`Ölçüm açıklaması satır ${row.name + 1}`} disabled={disabled} autoSize={{ minRows: 1, maxRows: 3 }} />
                  </Form.Item>
                ),
              },
              {
                title: 'Ölçüm Yapılan Ortamın Durumu',
                width: 180,
                render: (_value, row) => (
                  <Form.Item name={[row.name, 'measurementEnvironmentCondition']}>
                    <Select
                      allowClear
                      disabled={disabled}
                      options={environmentConditionOptions}
                      placeholder='Seçiniz'
                    />
                  </Form.Item>
                ),
              },
              {
                title: 'Varsa Devrede RCD (mA)',
                width: 160,
                render: (_value, row) => (
                  <Form.Item name={[row.name, 'rcdRatingMa']} rules={[nonNegativeNumericTextRule]}>
                    <AutoComplete
                      allowClear
                      aria-label={`Varsa Devrede RCD (mA) satır ${row.name + 1}`}
                      disabled={disabled}
                      options={rcdRatingOptions}
                      placeholder='Seçiniz veya değer giriniz'
                      suffixIcon={<DownOutlined />}
                      virtual={false}
                      className='earthing-measurements-table__number-input'
                      onChange={() => validateRcdDependents(row.name)}
                    />
                  </Form.Item>
                ),
              },
              {
                title: 'Varsa Devrede Toroid (A)',
                width: 160,
                render: (_value, row) => renderNumberInput(row.name, 'toroidRatingA'),
              },
              {
                title: 'Koruma İletkeni Kesiti mm²',
                width: 170,
                render: (_value, row) => (
                  <Form.Item
                    name={[row.name, 'protectiveConductorCrossSectionMm2']}
                    rules={[nonNegativeNumericTextRule]}
                  >
                    <AutoComplete
                      allowClear
                      aria-label={`Koruma İletkeni Kesiti (mm²) satır ${row.name + 1}`}
                      disabled={disabled}
                      options={protectiveConductorCrossSectionOptions}
                      placeholder='Seçiniz veya değer giriniz'
                      suffixIcon={<DownOutlined />}
                      virtual={false}
                      className='earthing-measurements-table__number-input'
                    />
                  </Form.Item>
                ),
              },
              {
                title: 'Koruma Elemanı',
                children: [
                  {
                    title: 'In (A)',
                    width: 130,
                    render: (_value, row) => renderNumberInput(row.name, 'ratedCurrentA'),
                  },
                  {
                    title: 'Açma Eğrisi Tipi veya Modeli',
                    width: 210,
                    render: (_value, row) => {
                      const currentValue = form.getFieldValue([
                        'measurements', row.name, 'tripCurveTypeOrModel',
                      ]);
                      const legacyValue = typeof currentValue === 'string' && currentValue !== ''
                        && !tripCurveTypeOrModelOptions.some(({ value: optionValue }) =>
                          optionValue === currentValue,
                        );
                      const options = legacyValue
                        ? [{ value: currentValue, label: `${currentValue} (eski kayıt)`, disabled: true },
                          ...tripCurveTypeOrModelOptions]
                        : tripCurveTypeOrModelOptions;
                      return (
                        <Form.Item name={[row.name, 'tripCurveTypeOrModel']}>
                          <Select
                            allowClear
                            aria-label={`Açma eğrisi satır ${row.name + 1}`}
                            disabled={disabled}
                            options={options}
                            placeholder='Seçiniz'
                          />
                        </Form.Item>
                      );
                    },
                  },
                ],
              },
              {
                title: 'Ölçülen Değer Zx(Rx)',
                width: 170,
                render: (_value, row) => (
                  <Form.Item noStyle shouldUpdate>
                    {() => (
                      <Form.Item
                        name={[row.name, 'phaseEarthLoopImpedanceOhm']}
                        getValueProps={(current) => ({ value: form.getFieldValue(
                          ['measurements', row.name, 'phaseEarthLoopImpedanceDash'],
                        ) ? '-' : current ?? '' })}
                        rules={[{ validator: (_rule, current) => {
                          const error = validateEarthingMeasuredValue(current);
                          return error ? Promise.reject(new Error(error)) : Promise.resolve();
                        } }]}
                      >
                        <Input aria-label={`Zx (Rx) satır ${row.name + 1}`} disabled={disabled} />
                      </Form.Item>
                    )}
                  </Form.Item>
                ),
              },
              {
                title: 'Varsa Devrede RCD Açma Akımı (mA)',
                width: 300,
                render: (_value, row) => renderRcdDependentInput(row.name, 'rcdTripCurrentMa'),
              },
              {
                title: 'Varsa Devrede RCD Açma Zamanı (ms)',
                width: 300,
                render: (_value, row) => renderRcdDependentInput(row.name, 'rcdTripTimeMs'),
              },
              {
                title: 'Sınır Değer Zs(Rs)',
                width: 150,
                align: 'center',
                render: (_value, row) => (
                  <Form.Item noStyle shouldUpdate>
                    {() => (
                      <Text>
                        {form.getFieldValue(['measurements', row.name, 'limitValueCalculationStatus'])
                          === 'MISSING_ENVIRONMENT'
                          ? 'EKSİK'
                          : form.getFieldValue(['measurements', row.name, 'limitValueZsRsOhm']) ?? '-'}
                      </Text>
                    )}
                  </Form.Item>
                ),
              },
              {
                title: 'Uygunluk',
                width: 250,
                align: 'center',
                render: (_value, row) => (
                  <Form.Item noStyle shouldUpdate>
                    {() => {
                      const current: EarthingMeasurement = form.getFieldValue([
                        'measurements', row.name,
                      ]) ?? {};
                      const manual = current.complianceOverridden === true;
                      const displayedCompliance = manual
                        ? current.compliance
                        : current.generatedCompliance ?? current.compliance;
                      return (
                        <div className='earthing-measurements-table__compliance'>
                          {renderCompliance(
                            displayedCompliance,
                            `Uygunluk sonucu satır ${row.name + 1}`,
                          ) ?? (
                            <Text
                              type='secondary'
                              aria-label={`Uygunluk sonucu satır ${row.name + 1}`}
                            >
                              -
                            </Text>
                          )}
                          <ComplianceNoteTooltip
                            note={manual ? current.compliance : null}
                            fullWidth
                            focusable={disabled}
                          >
                            <Select
                              aria-label={`Uygunluk notu satır ${row.name + 1}`}
                              disabled={disabled}
                              allowClear
                              className='earthing-measurements-table__compliance-select'
                              placeholder='Manuel not seçin'
                              value={manual && isManualCompliance(current.compliance)
                                ? current.compliance : undefined}
                              options={manualComplianceOptions}
                              onChange={(selected?: ManualEarthingCompliance) => {
                                form.setFieldValue(
                                  ['measurements', row.name, 'compliance'],
                                  selected ?? current.generatedCompliance ?? null,
                                );
                                form.setFieldValue(
                                  ['measurements', row.name, 'complianceOverridden'],
                                  selected !== undefined,
                                );
                                emitCurrentValue(form.getFieldsValue(true));
                              }}
                            />
                          </ComplianceNoteTooltip>
                        </div>
                      );
                    }}
                  </Form.Item>
                ),
              },
              {
                title: 'İşlemler',
                width: 100,
                fixed: 'right',
                align: 'center',
                render: (_value, row) => (
                  <Space size={4}>
                    <Tooltip title='Ölçüm satırını kopyala'>
                      <Button
                        type='text'
                        disabled={disabled}
                        icon={<CopyOutlined />}
                        aria-label='Ölçüm satırını kopyala'
                        onClick={() => {
                          const source = form.getFieldValue(['measurements', row.name]);
                          if (!source) return;
                          add(copyMeasurement(networkType, source));
                          emitAfterListOperation();
                        }}
                      />
                    </Tooltip>
                    <Popconfirm
                      title='Bu ölçüm satırını silmek istediğinize emin misiniz?'
                      okText='Sil'
                      cancelText='Vazgeç'
                      onConfirm={() => {
                        remove(row.name);
                        emitAfterListOperation();
                      }}
                      disabled={disabled}
                    >
                      <Tooltip title='Ölçüm satırını sil'>
                        <Button
                          type='text'
                          danger
                          disabled={disabled}
                          icon={<DeleteOutlined />}
                          aria-label='Ölçüm satırını sil'
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                ),
              },
            ];
            const totalWidth = (items: ColumnsType<TableRow>): number => items.reduce(
              (sum, column) => sum + ('children' in column
                ? totalWidth(column.children) : Number(column.width)),
              0,
            );

            return (
              <Space direction='vertical' size={12} className='earthing-measurements-table__content'>
                <Table<TableRow>
                  bordered
                  size='small'
                  pagination={false}
                  tableLayout='fixed'
                  columns={columns}
                  dataSource={dataSource}
                  scroll={{ x: totalWidth(columns) }}
                  locale={{ emptyText: 'Henüz ölçüm satırı eklenmedi.' }}
                />
                <Button
                  type='dashed'
                  icon={<PlusOutlined />}
                  disabled={disabled}
                  onClick={() => {
                    add({
                      generatedCompliance: null,
                      compliance: null,
                      complianceOverridden: false,
                      phaseEarthLoopImpedanceDash: false,
                      clientKey: crypto.randomUUID(),
                    });
                    emitAfterListOperation();
                  }}
                >
                  Satır Ekle
                </Button>
              </Space>
            );
          }}
        </Form.List>
      </Form>
    </section>
  );
});

EarthingMeasurementsTable.displayName = 'EarthingMeasurementsTable';

export default EarthingMeasurementsTable;
